import { Controller } from "@hotwired/stimulus";
import RequestHelper from "../helpers/request_helper";
import createFromModal from "../helpers/modal_create";

const SEARCH_DELAY = 500;

// The "Enhance Your Order?" step of the gift-build flow.
//
// Two things here are dead but harmless: the `searchLoading` target, whose element no view
// renders (guarded with hasSearchLoadingTarget), and `li.no-campaign` in the results click
// selector, a class nothing on this page ever emits.
//
// The results list mixes server-rendered `<li class="campaign">`s with ones this controller
// appends after a live search, so the click action is wired on the results container itself
// (not on each `<li>`) -- it still catches clicks on lis appended after connect.
export default class extends Controller {
  static get targets() {
    return [
      "form", "campaignField", "questionnaireField", "schedulerField",
      "campaignSelectText", "surveySelectText", "meetingSelectText",
      "campaignSearchInput", "campaignSearchResults", "searchLoading"
    ];
  }

  static get classes() {
    return ["selected"];
  }

  disconnect() {
    clearTimeout(this.searchTimer);
  }

  // Submits form
  nextStep(event) {
    event.preventDefault();
    this.formTarget.requestSubmit();
  }

  // Clears survey and campaign id (if added) and submits form to ignore step
  skipStep(event) {
    event.preventDefault();
    this.campaignFieldTarget.value = "";
    this.questionnaireFieldTarget.value = "";
    this.schedulerFieldTarget.value = "";
    this.formTarget.requestSubmit();
  }

  // Creates the campaign, then saves it on the build
  createCampaign(event) {
    createFromModal(event, {
      modal: "#create-campaign-modal",
      key: "campaign",
      keepDisabled: true,
      onCreated: (record) => {
        this.campaignFieldTarget.value = record.id;
        document.querySelector("input[name='repeat_step']").value = "true";
        this.formTarget.requestSubmit();
      },
    });
  }

  // Rewrites the hidden campaign_id field and updates the build with the campaign. Wired as a
  // click action on the results container (see the class comment), so the target li is derived
  // from the click rather than passed in.
  selectCampaign(event) {
    const li = event.target.closest("li.campaign, li.no-campaign");
    if (!li) { return; }
    event.preventDefault();
    this.campaignSelectTextTarget.textContent = li.textContent;
    this.campaignFieldTarget.value = li.dataset.id;
    Array.from(li.parentElement.children).forEach((sibling) => {
      if (sibling !== li) { sibling.classList.remove(this.selectedClass); }
    });
    li.classList.add(this.selectedClass);
  }

  // Performs a search and populates the results box
  search(event) {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.performSearch(event), SEARCH_DELAY);
  }

  performSearch(event) {
    event.stopPropagation();
    const input = this.campaignSearchInputTarget;
    const text = input.value;
    const url = `${input.dataset.url}?q=${text}`;
    if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = ""; }

    const options = RequestHelper.defaultRequestOptions();

    fetch(url, options).then((response) => response.json()).then((data) => {
      this.campaignSearchResultsTarget.querySelectorAll("li.campaign-category, li.campaign, li.no-results").forEach((node) => node.remove());

      if (data.campaigns.length > 0) {
        const grouped = {};
        data.campaigns.forEach((campaign) => {
          (grouped[campaign.category] = grouped[campaign.category] || []).push(campaign);
        });

        Object.entries(grouped).forEach(([category, campaigns]) => {
          const categoryLi = document.createElement("li");
          categoryLi.className = "campaign-category";
          categoryLi.textContent = category.replace("_", " ");
          this.campaignSearchResultsTarget.appendChild(categoryLi);

          campaigns.forEach((campaign) => {
            const campaignLi = document.createElement("li");
            campaignLi.className = "campaign";
            campaignLi.dataset.id = campaign.id;
            const nameSpan = document.createElement("span");
            nameSpan.textContent = campaign.name;
            campaignLi.appendChild(nameSpan);
            this.campaignSearchResultsTarget.appendChild(campaignLi);
          });
        });
      } else {
        const noResultsLi = document.createElement("li");
        noResultsLi.className = "no-results";
        noResultsLi.textContent = "No campaigns found.";
        this.campaignSearchResultsTarget.appendChild(noResultsLi);
      }
      if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = "none"; }
    }).catch((error) => {
      console.warn(error);
      if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = "none"; }
    });
  }

  // Creates the scheduler, then saves it on the build
  createScheduler(event) {
    createFromModal(event, {
      modal: "#create-scheduler-modal",
      key: "scheduler",
      keepDisabled: true,
      onCreated: (record) => {
        this.schedulerFieldTarget.value = record.id;
        document.querySelector("input[name='repeat_step']").value = "true";
        this.formTarget.requestSubmit();
      },
    });
  }

  toggleEnhancement(event) {
    const checkbox = event.currentTarget;
    const formGroup = checkbox.closest(".enhancement").querySelector(".enhancement-actions .form-group");

    if (checkbox.checked) {
      formGroup.style.display = "";
    } else {
      document.getElementById(checkbox.id.replace("toggle_", "")).value = ""; // Remove ID from updateFormHidden[*]Field
      formGroup.style.display = "none";
    }
  }

  selectSurvey(event) {
    event.preventDefault();
    const selection = event.currentTarget;
    this.surveySelectTextTarget.textContent = selection.textContent;
    Array.from(selection.parentElement.children).forEach((sibling) => {
      if (sibling !== selection) { sibling.classList.remove(this.selectedClass); }
    });
    selection.classList.add(this.selectedClass);
    this.questionnaireFieldTarget.value = selection.dataset.questionnaireId;
  }

  selectMeeting(event) {
    event.preventDefault();
    const selection = event.currentTarget;
    this.meetingSelectTextTarget.textContent = selection.textContent;
    Array.from(selection.parentElement.children).forEach((sibling) => {
      if (sibling !== selection) { sibling.classList.remove(this.selectedClass); }
    });
    selection.classList.add(this.selectedClass);
    this.schedulerFieldTarget.value = selection.dataset.schedulerId;
  }
}
